/**
 * Minimal ProseMirror-JSON → HTML walker.
 *
 * `@tiptap/html`'s `generateHTML(json, extensions)` would be the
 * official path, but it isn't in `node_modules` for this monorepo
 * yet. To avoid adding a runtime dep before the user has reviewed
 * it, we walk the JSON tree by hand and emit the StarterKit-shaped
 * HTML the page editor produces.
 *
 * Covered nodes:
 *   doc · paragraph · heading · text (with marks) · bulletList ·
 *   orderedList · listItem · codeBlock · blockquote · horizontalRule
 *   · hardBreak · image · table · tableRow · tableCell · tableHeader
 *
 * Covered marks:
 *   bold · italic · underline · strike · code · link
 *
 * Anything outside this set is rendered as inner content only,
 * so the surrounding paragraph still appears (i.e. unsupported
 * nodes degrade to plain text rather than disappearing).
 *
 * Output is intentionally NOT sanitized — the input comes from a
 * trusted Tiptap editor under the same admin app's CSRF context.
 * If we ever accept Tiptap JSON from untrusted sources, run it
 * through `dompurify` BEFORE rendering.
 */

import { safeUrl, safeImageUrl } from '@repo/utils/common';

type Json = any;

const escapeMap: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => escapeMap[c]!);
}

/**
 * Normalize intentional whitespace runs in body text. Content pasted straight
 * from MS Word fakes column layout with long runs of spaces / nbsp / tabs
 * ("၁။   ကဗျာနာမည်            စာဆို") — plain spaces collapse to nothing in
 * HTML, while Word's nbsp runs render as ragged, unbreakable 10–30-char gaps
 * that overflow on mobile. Both look broken. Replace every whitespace run of
 * 2+ with a UNIFORM small gap (nbsp-padded, ending in a normal space so the
 * line can still wrap there): columns read consistently on desktop and wrap
 * cleanly on narrow screens. Applied to TEXT nodes only (never attributes,
 * never code blocks — <pre> keeps its own spacing).
 */
function preserveSpaceRuns(s: string): string {
  return s.replace(/[\t \u00A0]{2,}/g, (run) =>
    run.length === 2 ? '\u00A0 ' : '\u00A0\u00A0\u00A0 ',
  );
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/**
 * Validate a CSS colour from a Tiptap `textStyle` / `highlight` mark before
 * it lands in an inline `style`. Restricts to hex / rgb(a) / hsl(a) / named
 * colours so an attacker-authored value can't break out of the declaration
 * (CSS injection). Returns null for anything unrecognised.
 */
function safeColor(c: unknown): string | null {
  if (typeof c !== 'string') return null;
  const v = c.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  if (/^rgba?\([\d.,\s%]+\)$/i.test(v)) return v;
  if (/^hsla?\([\d.,\s%degrad]+\)$/i.test(v)) return v;
  if (/^[a-zA-Z]{1,30}$/.test(v)) return v; // named colours (red, etc.)
  return null;
}

function applyMarks(text: string, marks: Json[] | undefined): string {
  if (!marks || marks.length === 0) return preserveSpaceRuns(escapeHtml(text));
  let html = preserveSpaceRuns(escapeHtml(text));
  for (const m of marks) {
    switch (m.type) {
      case 'bold':
      case 'strong':
        html = `<strong>${html}</strong>`;
        break;
      case 'italic':
      case 'em':
        html = `<em>${html}</em>`;
        break;
      case 'underline':
        html = `<u>${html}</u>`;
        break;
      case 'strike':
      case 'strikethrough':
        html = `<s>${html}</s>`;
        break;
      case 'code':
        html = `<code>${html}</code>`;
        break;
      case 'link': {
        // URL scheme guard FIRST — blocks `javascript:` and other
        // exec-on-click schemes before HTML-escaping. The escape on
        // its own doesn't help: `javascript:alert(1)` has no chars
        // to escape and would render as a working onclick payload.
        const href = escapeAttr(safeUrl(m.attrs?.href));
        const target = m.attrs?.target
          ? ` target="${escapeAttr(m.attrs.target)}"`
          : '';
        const rel =
          m.attrs?.target === '_blank'
            ? ' rel="noopener noreferrer"'
            : '';
        html = `<a href="${href}"${target}${rel}>${html}</a>`;
        break;
      }
      case 'textStyle': {
        // Color extension stores the picked colour here.
        const color = safeColor(m.attrs?.color);
        if (color) html = `<span style="color:${color}">${html}</span>`;
        break;
      }
      case 'highlight': {
        // Highlight extension (multicolor) stores the bg colour here.
        const color = safeColor(m.attrs?.color);
        html = color
          ? `<mark style="background-color:${color}">${html}</mark>`
          : `<mark>${html}</mark>`;
        break;
      }
      // Unknown marks ignored.
    }
  }
  return html;
}

function renderInline(nodes: Json[] | undefined): string {
  if (!nodes) return '';
  return nodes.map(renderNode).join('');
}

function renderChildren(nodes: Json[] | undefined): string {
  if (!nodes) return '';
  return nodes.map(renderNode).join('');
}

/**
 * Tiptap's TextAlign extension stores the chosen alignment as a
 * `textAlign` attr on `paragraph` / `heading` nodes (it's how the editor
 * centers table-header cells, justifies body copy, etc.). The renderer must
 * mirror it as an inline `text-align` style or the public page silently
 * drops the alignment. Whitelisted to the four valid values.
 */
function alignStyle(node: Json): string {
  const a = node.attrs?.textAlign;
  return typeof a === 'string' &&
    ['left', 'center', 'right', 'justify'].includes(a)
    ? ` style="text-align:${a}"`
    : '';
}

function renderNode(node: Json): string {
  if (!node || typeof node !== 'object') return '';
  switch (node.type) {
    case 'doc':
      return renderChildren(node.content);
    case 'paragraph': {
      const inner = renderInline(node.content);
      // Word-pasted docs are full of blank spacer paragraphs (empty or
      // whitespace-only). Tag them `data-empty` so CSS can shrink them to a
      // modest gap instead of a full blank line + margins — see globals.css.
      const isBlank =
        !inner ||
        inner.replace(/&nbsp;|\u00A0|\s/g, '').length === 0;
      if (isBlank) return `<p data-empty="true"></p>`;
      return `<p${alignStyle(node)}>${inner}</p>`;
    }
    case 'heading': {
      const lvl = Math.max(1, Math.min(6, Number(node.attrs?.level) || 1));
      return `<h${lvl}${alignStyle(node)}>${renderInline(node.content)}</h${lvl}>`;
    }
    case 'text': {
      return applyMarks(node.text ?? '', node.marks);
    }
    case 'bulletList':
      return `<ul>${renderChildren(node.content)}</ul>`;
    case 'orderedList': {
      const start = node.attrs?.start ? ` start="${node.attrs.start}"` : '';
      return `<ol${start}>${renderChildren(node.content)}</ol>`;
    }
    case 'listItem':
      return `<li>${renderChildren(node.content)}</li>`;
    case 'codeBlock': {
      const lang = node.attrs?.language
        ? ` class="language-${escapeAttr(node.attrs.language)}"`
        : '';
      // codeBlock children are usually a single text node; pass
      // through `applyMarks` so `<code>` text is escaped properly.
      const code = node.content
        ? node.content
            .map((c: any) => escapeHtml(c.text ?? ''))
            .join('')
        : '';
      return `<pre><code${lang}>${code}</code></pre>`;
    }
    case 'blockquote':
      return `<blockquote>${renderChildren(node.content)}</blockquote>`;
    case 'horizontalRule':
    case 'horizontal_rule':
      return '<hr />';
    case 'hardBreak':
    case 'hard_break':
      return '<br />';
    case 'image': {
      // Stricter than safeUrl: blocks `data:` URLs too (SVG-with-
      // script smuggling). Empty src renders broken instead of
      // executing a payload.
      const src = escapeAttr(safeImageUrl(node.attrs?.src));
      const alt = escapeAttr(node.attrs?.alt ?? '');
      const title = node.attrs?.title
        ? ` title="${escapeAttr(node.attrs.title)}"`
        : '';
      const mediaId = node.attrs?.mediaId
        ? ` data-media-id="${escapeAttr(String(node.attrs.mediaId))}"`
        : '';

      // Presentation attrs (added in MediaAwareImage). Authors set
      // these from the admin's floating image toolbar; the public
      // renderer mirrors them via inline styles + data-attrs the
      // client-side Lightbox component can pick up.
      const align = ['left', 'center', 'right'].includes(node.attrs?.align)
        ? node.attrs.align
        : 'center';
      const bordered = !!node.attrs?.bordered;
      const padded = !!node.attrs?.padded;
      const lightbox = node.attrs?.lightbox !== false; // default true
      const widthRaw = Number(node.attrs?.width);
      const widthPct =
        Number.isFinite(widthRaw) && widthRaw > 0 && widthRaw <= 100
          ? widthRaw
          : 100;
      const caption = node.attrs?.caption
        ? escapeHtml(String(node.attrs.caption))
        : '';

      const figureStyle: string[] = [];
      if (align === 'left') {
        figureStyle.push('text-align:left', 'margin-left:0', 'margin-right:auto');
      } else if (align === 'right') {
        figureStyle.push('text-align:right', 'margin-left:auto', 'margin-right:0');
      } else {
        figureStyle.push('text-align:center', 'margin-left:auto', 'margin-right:auto');
      }
      figureStyle.push(`max-width:${widthPct}%`);

      const imgStyle: string[] = ['width:100%', 'height:auto'];
      if (bordered) {
        imgStyle.push('border:1px solid #d4d4d8', 'border-radius:4px');
      }
      if (padded) {
        imgStyle.push('padding:8px', 'background:#f4f4f5', 'box-sizing:border-box');
      }
      if (lightbox) {
        imgStyle.push('cursor:zoom-in');
      }

      const imgAttrs: string[] = [
        `src="${src}"`,
        `alt="${alt}"`,
        `loading="lazy"`,
        `decoding="async"`,
        `style="${imgStyle.join(';')}"`,
      ];
      if (lightbox) imgAttrs.push('data-lightbox="true"');
      if (title) imgAttrs.push(title.trim());
      if (mediaId) imgAttrs.push(mediaId.trim());

      const capHtml = caption
        ? `<figcaption style="font-size:0.875rem;color:#71717a;margin-top:6px;text-align:${align}">${caption}</figcaption>`
        : '';

      return (
        `<figure data-tiptap-image="true" data-align="${align}" data-width="${widthPct}"` +
        (bordered ? ' data-bordered="true"' : '') +
        (padded ? ' data-padded="true"' : '') +
        (lightbox ? '' : ' data-lightbox="false"') +
        ` style="${figureStyle.join(';')}">` +
        `<img ${imgAttrs.join(' ')} />` +
        capHtml +
        `</figure>`
      );
    }
    // ── Tables (StarterKit + extension-table) ──────────────────
    // Tiptap stores tables as `table > tableRow > (tableCell |
    // tableHeader) > <block content>`. Cells may carry `colspan` /
    // `rowspan` / `colwidth` attrs; we pass colspan + rowspan
    // through and translate colwidth (an array of px widths) onto
    // the cell's inline style so column sizing survives. Without
    // this case the entire table was silently dropped on the
    // public site even though the editor preserved it.
    case 'table':
      // Wrap in a scroll container so the table fills the article
      // width AND wide tables can horizontally scroll on narrow
      // viewports without forcing the table itself to `display:
      // block` (which collapses it to content width). Pair with
      // the `.rich-table-wrap` rule in globals.css.
      return `<div class="rich-table-wrap"><table>${renderChildren(node.content)}</table></div>`;
    case 'tableRow':
      return `<tr>${renderChildren(node.content)}</tr>`;
    case 'tableCell':
    case 'tableHeader': {
      const tag = node.type === 'tableHeader' ? 'th' : 'td';
      const attrs: string[] = [];
      const cs = Number(node.attrs?.colspan);
      const rs = Number(node.attrs?.rowspan);
      if (cs && cs > 1) attrs.push(`colspan="${cs}"`);
      if (rs && rs > 1) attrs.push(`rowspan="${rs}"`);
      const cw = node.attrs?.colwidth;
      if (Array.isArray(cw) && cw[0] && Number(cw[0]) > 0) {
        attrs.push(`style="width:${Number(cw[0])}px"`);
      }
      const open = attrs.length ? `<${tag} ${attrs.join(' ')}>` : `<${tag}>`;
      return `${open}${renderChildren(node.content)}</${tag}>`;
    }
    default:
      // Unknown — render any children inline so we don't lose text.
      return renderChildren(node.content);
  }
}

/**
 * Convert a Tiptap ProseMirror JSON document to HTML. Returns ''
 * for null / non-object input.
 */
export function tiptapJsonToHtml(json: unknown): string {
  if (!json || typeof json !== 'object') return '';
  return renderNode(json);
}

/** A body value is "empty" when its rendered HTML has no visible text or media
 * — covers undefined, empty strings, AND empty Tiptap docs ({type:'doc',content:[]}
 * or a single blank paragraph), which a plain `??`/`||` check would wrongly keep. */
function bodyHtmlIsEmpty(html: string): boolean {
  if (!html) return true;
  // Keep media (img/iframe/video/embed/hr) as "content"; otherwise strip tags
  // + entities + whitespace and see if anything visible remains.
  if (/<(img|iframe|video|source|embed|hr|table)\b/i.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .trim();
  return text.length === 0;
}

function bodyToHtml(value: unknown, contentFormat: string): string {
  if (value == null) return '';
  if (contentFormat === 'json') return tiptapJsonToHtml(value);
  return typeof value === 'string' ? value : String(value);
}

/**
 * Pick a post/page body to render for the current language WITH bidirectional
 * fallback: if the requested language's body is empty (blank string OR empty
 * Tiptap doc), fall back to the other language — and vice-versa. Returns the
 * rendered HTML (already converted from Tiptap JSON when needed) or '' when no
 * language has content. `bodyMap` is the multilingual { en, mm } body object.
 */
export function pickLocalizedBodyHtml(
  bodyMap: any,
  currentLanguage: string,
  contentFormat: string = 'html',
): string {
  if (!bodyMap || typeof bodyMap !== 'object') {
    // Non-multilingual body (already a string / single doc) — render as-is.
    const html = bodyToHtml(bodyMap, contentFormat);
    return bodyHtmlIsEmpty(html) ? '' : html;
  }
  const other = currentLanguage === 'mm' ? 'en' : 'mm';
  // Try the requested language first, then the other, then en/mm as a backstop.
  for (const lang of [currentLanguage, other, 'en', 'mm']) {
    const html = bodyToHtml(bodyMap[lang], contentFormat);
    if (!bodyHtmlIsEmpty(html)) return html;
  }
  return '';
}
